import React, { useEffect, useState, useId } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { monthNames } from '../../i18n/i18n';
import { getCashFlow } from '../../services/cashflow';
import { onQuerySuccess } from '../../utils/queryResponse';
import { rsdFormat } from '../../utils/helper';
import { SkeletonChart } from '../skeleton';

/* The approved "Revenue — last 6 months" area chart.
   Real data: /cash-flow/monthly already returns monthly completed revenue
   (chart.income.data) — we request 12 months so we can also show the
   "vs prior 6 months" delta. Frontend-only; no API change. RSD throughout. */

const intFmt = (v) => Math.round(Number(v) || 0).toLocaleString('sr-RS');

/* label coming back as "MM YY" → month index (0-11) */
const monthIdx = (label) => {
    const mm = parseInt(String(label).trim().slice(0, 2), 10);
    return isNaN(mm) ? null : (mm - 1 + 12) % 12;
};

/* a "nice" axis step so 3 gridlines land on round numbers */
const niceStep = (x) => {
    if (x <= 0) return 100;
    const p = Math.pow(10, Math.floor(Math.log10(x)));
    const n = x / p;
    const f = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
    return f * p;
};

const H = 210;           // chart height (px / svg units)
const PAD_TOP = 14;
const PAD_BOT = 8;
const PLOT = H - PAD_TOP - PAD_BOT;

const RevenueAreaChart = () => {
    const { t } = useTranslation();
    const months = monthNames('short');
    const monthsFull = monthNames();
    const gid = useId().replace(/:/g, '');
    const [series, setSeries] = useState(null); // { values:[], labels:[], prev:[] }
    const [selectedIndex, setSelectedIndex] = useState(null); // a clicked month, or null = whole 6-month view

    const [range] = useState(() => {
        const end = new Date();
        const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
        return { start, end };
    });

    const { data } = useQuery(
        ['overview-revenue-monthly', range.start, range.end],
        () => getCashFlow('monthly', range.start, range.end),
        { keepPreviousData: true, staleTime: 1000 * 60, refetchOnMount: true }
    );

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            const labels = result?.chart?.income?.labels ?? [];
            const values = (result?.chart?.income?.data ?? []).map((v) => Number(v) || 0);
            setSeries({
                values: values.slice(-6),
                labels: labels.slice(-6),
                prev: values.slice(-12, -6),
            });
        });
    }, [data]);

    if (!series) return <div style={{ height: H + 70 }}><SkeletonChart /></div>;

    const { values, labels, prev } = series;
    const total = values.reduce((a, b) => a + b, 0);

    if (!total) {
        return (
            <>
                <div className="chh"><div><div className="t">{t('charts.revenueLast6Months', 'Revenue — last 6 months')}</div><div className="s">{t('charts.paidInvoicesByMonth', 'Paid invoices by month')}</div></div></div>
                <div className="sbr-empty" style={{ height: H }}><span>{t('charts.noCompletedRevenue', 'No completed revenue in the last 6 months')}</span></div>
            </>
        );
    }

    // scale + gridlines
    const maxVal = Math.max(...values);
    const step = niceStep(maxVal / 3);
    const niceMax = step * 3;
    const y = (v) => PAD_TOP + (1 - v / niceMax) * PLOT;
    const x = (i) => (values.length === 1 ? 360 : (i / (values.length - 1)) * 720);
    const gridlines = [1, 2, 3].map((k) => ({ val: step * k, top: y(step * k) }));

    const linePath = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
    const areaPath = `${linePath} L720,${H} L0,${H} Z`;

    // header / footer figures
    const prevTotal = prev.reduce((a, b) => a + b, 0);
    const delta = prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : null;
    const avg = total / values.length;
    const bestI = values.indexOf(maxVal);
    const bestLabel = monthIdx(labels[bestI]) != null ? monthsFull[monthIdx(labels[bestI])] : '';
    const firstM = monthIdx(labels[0]); const lastM = monthIdx(labels[labels.length - 1]);
    const subtitle = (firstM != null && lastM != null) ? t('charts.paidInvoicesRange', 'Paid invoices, {{from}} through {{to}}', { from: monthsFull[firstM], to: monthsFull[lastM] }) : t('charts.paidInvoicesByMonth', 'Paid invoices by month');

    /* ── selection: clicking a month drills the card into that month ── */
    const sel = (selectedIndex != null && selectedIndex >= 0 && selectedIndex < values.length) ? selectedIndex : null;
    const toggleMonth = (i) => setSelectedIndex((p) => (p === i ? null : i));

    const headValue = sel != null ? values[sel] : total;
    let headDelta, headDeltaText;
    if (sel != null) {
        const prevVal = sel > 0 ? values[sel - 1] : (prev.length ? prev[prev.length - 1] : 0);
        headDelta = prevVal > 0 ? ((values[sel] - prevVal) / prevVal) * 100 : null;
        const pIdx = sel > 0
            ? monthIdx(labels[sel - 1])
            : (monthIdx(labels[0]) != null ? (monthIdx(labels[0]) + 11) % 12 : null);
        headDeltaText = pIdx != null ? t('charts.vsMonth', 'vs {{month}}', { month: months[pIdx] }) : '';
    } else {
        headDelta = delta;
        headDeltaText = t('charts.vsPrior6Months', 'vs prior 6 months');
    }
    const selMonth = sel != null ? monthsFull[monthIdx(labels[sel])] : null;
    const headTitle = sel != null ? t('charts.revenueMonth', 'Revenue — {{month}}', { month: selMonth }) : t('charts.revenueLast6Months', 'Revenue — last 6 months');
    const headSub = sel != null ? t('charts.paidInvoicesIn', 'Paid invoices in {{month}}', { month: selMonth }) : subtitle;

    return (
        <>
            <div className="chh">
                <div>
                    <div className="t">
                        {headTitle}
                        {sel != null && <button type="button" className="rev-clear" onClick={() => setSelectedIndex(null)}>{t('charts.allSixMonths', '← all 6 months')}</button>}
                    </div>
                    <div className="s">{headSub}</div>
                </div>
                <div className="big">
                    <div className="v">{rsdFormat(headValue)}</div>
                    {headDelta != null &&
                        <div className={`d ${headDelta < 0 ? 'down' : ''}`}>
                            {headDelta < 0 ? '▼' : '▲'} {Math.abs(headDelta).toFixed(1).replace('.', ',')}% {headDeltaText}
                        </div>}
                </div>
            </div>

            <div className="legend-row" style={{ margin: '6px 0 12px' }}>
                <span><i style={{ background: 'var(--brand)' }} />{t('charts.monthlyPaidRevenue', 'Monthly paid revenue')}</span>
            </div>

            <div className="area-wrap" style={{ height: H }}>
                {gridlines.map((g, i) => (
                    <div key={i} className="gl" style={{ top: g.top }}>{intFmt(g.val)}</div>
                ))}
                <svg className="area" viewBox={`0 0 720 ${H}`} preserveAspectRatio="none" style={{ height: H }}>
                    <defs>
                        <linearGradient id={`rev${gid}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#3B74F2" stopOpacity="0.28" />
                            <stop offset="100%" stopColor="#3B74F2" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={areaPath} fill={`url(#rev${gid})`} />
                    <path d={linePath} fill="none" stroke="#1D4FD8" strokeWidth="2.6"
                        strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                </svg>
                {/* dots as HTML so they stay perfectly round under the stretched svg */}
                {values.map((v, i) => (
                    <div key={i}
                        className={`rev-dot${i === values.length - 1 ? ' last' : ''}${sel === i ? ' selected' : ''}`}
                        style={{ left: `${(x(i) / 720) * 100}%`, top: y(v) }}
                        onClick={() => toggleMonth(i)}
                        title={`${monthsFull[monthIdx(labels[i])] || ''}: ${rsdFormat(v)}`} />
                ))}
            </div>

            <div className="xlabels">
                {labels.map((l, i) => (
                    <span key={i} className={`xlbl${sel === i ? ' selected' : ''}`} onClick={() => toggleMonth(i)}>
                        {monthIdx(l) != null ? months[monthIdx(l)] : ''}
                    </span>
                ))}
            </div>

            <div className="area-cap">
                <span>{t('charts.bestMonth', 'Best month:')} <b>{bestLabel} — {rsdFormat(maxVal)}</b></span>
                <span>{t('charts.sixMonthAverage', '6-month average:')} <b>{rsdFormat(avg)}</b></span>
            </div>
        </>
    );
};

export default RevenueAreaChart;
